import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { MessageSquare, X, Send, Image as ImageIcon, Bug, Lightbulb, AlertTriangle } from 'lucide-react';
import { fetchWithAuth } from '../services/TokenRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPES = [
  { value: 'bug', label: 'Bug', icon: Bug, color: '#dc2626' },
  { value: 'feature', label: 'Feature request', icon: Lightbulb, color: '#d97706' },
  { value: 'suggestion', label: 'Suggestion', icon: MessageSquare, color: '#2563eb' },
  { value: 'other', label: 'Other', icon: AlertTriangle, color: '#64748b' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

/**
 * Modal used from Settings → Send Feedback. Multipart POST so the
 * screenshot upload rides along on the same request.
 */
const FeedbackModal = ({ open, onClose }) => {
  const [type, setType] = useState('bug');
  const [priority, setPriority] = useState('medium');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [preview, setPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Revoke the current preview blob URL whenever it's replaced or the
  // modal unmounts. Without this each attach/remove cycle leaks a blob
  // into the browser's memory table until the tab is closed.
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  if (!open) return null;

  const reset = () => {
    setType('bug');
    setPriority('medium');
    setTitle('');
    setDescription('');
    setScreenshot(null);
    // Explicitly revoke here too — the effect covers replacement but
    // reset() is called before the modal is fully hidden.
    if (preview) URL.revokeObjectURL(preview);
    setPreview('');
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    setScreenshot(f);
    // Drop any prior preview URL before installing the new one.
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('type', type);
      fd.append('priority', priority);
      fd.append('title', title.trim());
      fd.append('description', description.trim());
      if (screenshot) fd.append('screenshot', screenshot);

      const resp = await fetchWithAuth(`${API}/feedback`, {
        method: 'POST',
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      toast.success('Feedback sent — thank you!');
      reset();
      onClose?.();
    } catch (err) {
      toast.error(typeof err.message === 'string' ? err.message : 'Failed to send feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '1rem',
      }}
      onClick={handleClose}
      data-testid="feedback-modal"
    >
      <div
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560,
          maxHeight: '90vh', overflowY: 'auto', padding: '1.75rem',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <MessageSquare size={22} color="#fff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
                Send Feedback
              </h2>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
                Suggestions and bug reports go straight to the team.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 4, color: '#64748b',
            }}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Type — pill grid */}
          <label style={labelStyle}>Type</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: '1rem' }}>
            {TYPES.map((t) => {
              const Icon = t.icon;
              const active = type === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setType(t.value)}
                  data-testid={`feedback-type-${t.value}`}
                  style={{
                    padding: '0.65rem 0.4rem',
                    borderRadius: 10,
                    border: `1.5px solid ${active ? t.color : '#e2e8f0'}`,
                    background: active ? `${t.color}12` : '#fff',
                    color: active ? t.color : '#475569',
                    cursor: 'pointer',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 4,
                    fontSize: '0.75rem', fontWeight: 600,
                    transition: 'all 0.15s',
                  }}
                >
                  <Icon size={18} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Priority — pill row */}
          <label style={labelStyle}>Priority</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
            {PRIORITIES.map((p) => {
              const active = priority === p.value;
              const color = p.value === 'high' ? '#dc2626' : p.value === 'medium' ? '#d97706' : '#64748b';
              return (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPriority(p.value)}
                  data-testid={`feedback-priority-${p.value}`}
                  style={{
                    flex: 1,
                    padding: '0.55rem',
                    borderRadius: 999,
                    border: `1.5px solid ${active ? color : '#e2e8f0'}`,
                    background: active ? `${color}18` : '#fff',
                    color: active ? color : '#475569',
                    cursor: 'pointer',
                    fontSize: '0.85rem', fontWeight: 600,
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          <label style={labelStyle}>Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="Short summary…"
            data-testid="feedback-title"
            style={inputStyle}
            required
          />

          <label style={labelStyle}>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={5000}
            placeholder="What happened? What did you expect?"
            data-testid="feedback-description"
            rows={5}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 120, fontFamily: 'inherit' }}
            required
          />

          <label style={labelStyle}>Screenshot (optional)</label>
          {preview ? (
            <div style={{ marginBottom: '1rem', position: 'relative' }}>
              <img
                src={preview}
                alt="Attached screenshot"
                style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8, border: '1px solid #e2e8f0' }}
              />
              <button
                type="button"
                onClick={() => {
                  setScreenshot(null);
                  if (preview) URL.revokeObjectURL(preview);
                  setPreview('');
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                data-testid="feedback-remove-screenshot"
                style={{
                  position: 'absolute', top: 6, right: 6,
                  background: '#0f172a', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '3px 8px', fontSize: '0.72rem',
                  cursor: 'pointer', fontWeight: 600,
                }}
              >
                Remove
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="feedback-attach-screenshot"
              style={{
                width: '100%',
                padding: '0.75rem',
                marginBottom: '1rem',
                borderRadius: 10,
                border: '1.5px dashed #cbd5e1',
                background: '#f8fafc',
                color: '#64748b',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                fontSize: '0.85rem', fontWeight: 600,
              }}
            >
              <ImageIcon size={16} />
              Attach a screenshot
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            style={{ display: 'none' }}
          />

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              style={secondaryBtn}
              data-testid="feedback-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="feedback-submit"
              style={{
                ...primaryBtn,
                opacity: submitting ? 0.6 : 1,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              <Send size={16} />
              {submitting ? 'Sending…' : 'Send Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const labelStyle = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#334155',
  marginBottom: 6,
  marginTop: 4,
};

const inputStyle = {
  width: '100%',
  padding: '0.65rem 0.85rem',
  borderRadius: 10,
  border: '1.5px solid #e2e8f0',
  fontSize: '0.9rem',
  color: '#0f172a',
  outline: 'none',
  marginBottom: '1rem',
  boxSizing: 'border-box',
};

const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  padding: '0.6rem 1.2rem', borderRadius: 10,
  background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
  color: '#fff', border: 'none', cursor: 'pointer',
  fontSize: '0.9rem', fontWeight: 600,
};

const secondaryBtn = {
  padding: '0.6rem 1.2rem', borderRadius: 10,
  background: '#f1f5f9', color: '#334155',
  border: 'none', cursor: 'pointer',
  fontSize: '0.9rem', fontWeight: 600,
};

export default FeedbackModal;
